import React from 'react';
import { Tag } from 'antd';

type Unit = 'H' | 'D' | 'W' | 'M' | 'Y';

/** Named presets for the detailLevel prop — how many time units to display. */
export const DaysAgoDetail = {
  /** Single unit: "3M" */
  COMPACT: 0,
  /** Two units: "1Y 3M" */
  SHORT: 1,
  /** Three units: "1Y 3M 2W" */
  MEDIUM: 2,
  /** Four units: "1Y 3M 2W 1D" */
  VERBOSE: 3,
} as const;

type DetailLevel = (typeof DaysAgoDetail)[keyof typeof DaysAgoDetail];

export interface DaysAgoProps {
  /** The reference date to measure from */
  date: string | Date;
  /** Defaults to now */
  compareDate?: string | Date;
  /** Show weeks (e.g. 1W 3D) instead of rolling days into months */
  showWeeks?: boolean;
  detailLevel?: DetailLevel;
  style?: React.CSSProperties;
  className?: string;
}

// Preset Ant Design named colors give nice pastel backgrounds.
// undefined = default gray tag (no color prop).
const UNIT_COLORS: Record<Unit, string | undefined> = {
  H: 'blue',
  D: 'green',
  W: 'gold',
  M: undefined,
  Y: undefined,
};

function hasTimeComponent(date: string | Date): boolean {
  if (date instanceof Date) return true;
  return /T\d{2}:\d{2}/.test(String(date));
}

function toDate(d: string | Date): Date {
  return d instanceof Date ? d : new Date(d);
}

function buildParts(
  totalMs: number,
  showWeeks: boolean,
  canShowHours: boolean,
): Array<[number, Unit]> {
  const totalHours = Math.floor(totalMs / (1000 * 60 * 60));
  const totalDays = Math.floor(totalHours / 24);

  const years = Math.floor(totalDays / 365);
  const rem1 = totalDays - years * 365;
  const months = Math.floor(rem1 / 30);
  const rem2 = rem1 - months * 30;

  let weeks = 0;
  let days = rem2;
  if (showWeeks) {
    weeks = Math.floor(rem2 / 7);
    days = rem2 - weeks * 7;
  }

  const hours = totalHours - totalDays * 24;

  const parts: Array<[number, Unit]> = [];
  if (years > 0) parts.push([years, 'Y']);
  if (months > 0) parts.push([months, 'M']);
  if (weeks > 0) parts.push([weeks, 'W']);
  if (days > 0) parts.push([days, 'D']);
  if (canShowHours && hours > 0) parts.push([hours, 'H']);

  // Fallback for < 1 day (or < 1 hour) with nothing to show yet
  if (parts.length === 0) {
    parts.push([0, canShowHours ? 'H' : 'D']);
  }

  return parts;
}

const DaysAgo: React.FC<DaysAgoProps> = ({
  date,
  compareDate,
  showWeeks = false,
  detailLevel = 0,
  style,
  className,
}) => {
  const from = toDate(date);
  if (isNaN(from.getTime())) return null;

  const to = compareDate ? toDate(compareDate) : new Date();
  const totalMs = Math.abs(to.getTime() - from.getTime());
  const canShowHours = hasTimeComponent(date);

  const allParts = buildParts(totalMs, showWeeks, canShowHours);
  const parts = allParts.slice(0, detailLevel + 1);

  const dominantUnit = parts[0]?.[1] ?? 'D';
  const color = UNIT_COLORS[dominantUnit];

  const label = parts
    .map(([v, u]) => (v === 0 ? `<1${u}` : `${v}${u}`))
    .join(' ');

  return (
    <Tag
      color={color}
      title={from.toLocaleString()}
      style={{ fontSize: 11, ...style }}
      className={className}
    >
      {label}
    </Tag>
  );
};

export default DaysAgo;
